import { useRef, useState } from "react";
import styles from "./PrivateUpdates.module.scss";

interface Props {
  updates: string[];
  loading: boolean;
  failed: boolean;
  hasData: boolean;
  available: boolean;
  retry: () => void;
  /* UI-DESIGN: red only when reading is the screen's main action (guests) */
  emphasis?: boolean;
}

// UI-DESIGN: new private information is an invitation to read, never a popup.
// While reading, hold a snapshot so polling cannot move text under a thumb.
export default function PrivateUpdates({ updates, loading, failed, hasData, available, retry, emphasis = true }: Props) {
  const [opened, setOpened] = useState(false);
  const [readUpdates, setReadUpdates] = useState<string[] | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const openButton = useRef<HTMLButtonElement>(null);
  const changed = JSON.stringify(updates) !== JSON.stringify(readUpdates ?? []);
  const ready = hasData && updates.length > 0;
  const open = () => {
    setReadUpdates([...updates]);
    setOpened(true);
    requestAnimationFrame(() => heading.current?.focus());
  };
  const close = () => {
    setOpened(false);
    requestAnimationFrame(() => openButton.current?.focus());
  };

  return (
    <section className={styles.paper} aria-labelledby="updates-heading">
      <div className={styles.top}>
        <h2 id="updates-heading" tabIndex={-1} ref={heading}>Your updates</h2>
        {opened && <button type="button" className={styles.quiet} onClick={close} aria-label="Hide my updates">Hide updates</button>}
      </div>
      <p className={styles.status} role="status">
        {!available ? "Your updates will be here once your character is ready."
          : failed ? hasData ? "Couldn’t check for updates. Your last received updates are still here." : "Couldn’t load your updates. Try again."
          : !hasData && loading ? "Checking your updates…"
          : !ready ? "Nothing new yet. You can leave this page open; we’ll check for you."
          : changed ? "New information is ready when you are."
          : "You’re caught up."}
      </p>
      {failed && <button type="button" className={styles.quiet} onClick={retry} disabled={loading}>{loading ? "Checking…" : "Try again"}</button>}
      {!opened && ready && <>
        <p className={styles.hint}>For your eyes only.</p>
        <button type="button" ref={openButton} className={emphasis ? styles.primary : styles.secondary} onClick={open}>{changed ? "Read your updates" : "Open your updates"}</button>
      </>}
      {opened && <>
        {changed && ready && <button type="button" className={styles.primary} onClick={open}>Read new updates</button>}
        <ol className={styles.entries}>
          {[...(readUpdates ?? [])].reverse().map((entry, index) => (
            <li key={`${index}:${entry}`}>
              <h3>Update {(readUpdates?.length ?? 0) - index}</h3>
              <p>{entry}</p>
            </li>
          ))}
        </ol>
      </>}
    </section>
  );
}
